import { AuthTokens } from "aws-amplify/auth";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

import PageLoader from "../../../components/loader/PageLoader";
import { customToast } from "../../../components/notify/NotifyContainer";
import PopupProvider from "../../../contexts/popupProvider";
import { useAmzChatWidget, useAppDispatch, useAppSelector } from "../../../hooks";
import { awsFetchUserInfo, awsGetCurrentUser, currentSession } from "../../../lib/aws";
import { getLocalValueByKey } from "../../../utils";
import {
    useAmazeSignInMutation,
    useAmzLogoutMutation,
    useCheckUserStatusMutation,
    useGetUserPermissionsQuery,
} from "../services";
import {
    postEnterpriseLogin,
    postLogin,
    postLogout,
    selectCurrentUser,
    selectIsLogin,
    setCurrentUser,
} from "../services/authSlice";
import { ICurrentUser } from "../types/interfaces";
import { getStoredEnterpriseUser, getUserRole } from "../utils";

const VerifyLogin = () => {
    useAmzChatWidget();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    // 權限清單載入失敗只提示一次，避免 effect 重跑時重複跳 toast。
    const permissionErrorNotifiedRef = useRef(false);
    const { pathname } = useLocation();
    const isFromTempLogin = pathname === "/temp-login";

    const backLoginPath = isFromTempLogin ? "/temp-login" : "/login";
    const matchEnterpriseLoginPath = useMatch("/enterprise-login");
    const amzAccessTokenFromEnterpriseLogin = getLocalValueByKey("amaze_accessToken");
    const amzRefreshTokenFromEnterpriseLogin = getLocalValueByKey("amaze_refreshToken");
    const enterpriseAccountId = getLocalValueByKey("enterprise_account_id");
    const enterpriseAmazeAccountId = getLocalValueByKey("enterprise_amaze_account_id");
    const enterpriseRoles = getLocalValueByKey("enterprise_roles");
    const enterpriseUserId = getLocalValueByKey("enterprise_user_id");
    const enterpriseIsFirstTimeLogin = getLocalValueByKey("enterprise_is_first_time_login");
    const isEnterpriseLogin =
        !!matchEnterpriseLoginPath || !!(amzAccessTokenFromEnterpriseLogin && amzRefreshTokenFromEnterpriseLogin);

    const isLogin = useAppSelector(selectIsLogin);
    const currentUser = useAppSelector(selectCurrentUser);

    const [amazeSignIn, { isLoading }] = useAmazeSignInMutation();
    const [userStatus, { isLoading: checkUserStatusLoading }] = useCheckUserStatusMutation();
    const [fetchAmzLogout] = useAmzLogoutMutation();
    const { data: usersRole, isError: userPermissionError } = useGetUserPermissionsQuery(undefined, {
        pollingInterval: 0,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        const getAmzSession = async () => {
            try {
                dispatch(
                    postEnterpriseLogin({
                        accessToken: amzAccessTokenFromEnterpriseLogin,
                        refreshToken: amzRefreshTokenFromEnterpriseLogin,
                    })
                );
                const res = await userStatus().unwrap();
                if (res) {
                    localStorage.setItem("enterprise_amaze_account_id", res.account_id);
                    const storedEnterpriseUser = getStoredEnterpriseUser({
                        enterpriseAmazeAccountId: res.account_id,
                        enterpriseAccountId,
                        enterpriseIsFirstTimeLogin,
                        enterpriseRoles,
                        enterpriseUserId,
                    });
                    const userInfo: ICurrentUser = {
                        accountId: res.account_id,
                        isFirstTimeLogin: res.is_first_time_login,
                        bindLine: res.bind_line,
                        email: "res.email",
                        username: "res.username",
                        enterpriseAccountId:
                            storedEnterpriseUser?.enterpriseAccountId || (enterpriseAccountId as string),
                        role: "enterprise_user",
                        enterpriseRoles: storedEnterpriseUser?.enterpriseRoles || null,
                        enterpriseUserId: storedEnterpriseUser?.enterpriseUserId || null,
                    };
                    dispatch(setCurrentUser(userInfo));
                }
                return;
            } catch (err) {
                navigate(backLoginPath);
                dispatch(postLogout());
            }
        };
        if (
            !isLogin &&
            !currentUser &&
            isEnterpriseLogin &&
            amzAccessTokenFromEnterpriseLogin &&
            amzRefreshTokenFromEnterpriseLogin
        ) {
            getAmzSession();
        }
    }, [
        amzAccessTokenFromEnterpriseLogin,
        amzRefreshTokenFromEnterpriseLogin,
        backLoginPath,
        currentUser,
        dispatch,
        enterpriseAccountId,
        enterpriseAmazeAccountId,
        enterpriseIsFirstTimeLogin,
        isEnterpriseLogin,
        isLogin,
        navigate,
        userStatus,
        enterpriseRoles,
        enterpriseUserId,
        usersRole,
    ]);

    useEffect(() => {
        const getAwsSession = async () => {
            try {
                const sessionRes: AuthTokens | null = await currentSession();

                const awsAccessToken = sessionRes?.accessToken.toString();

                if (!awsAccessToken) {
                    navigate(backLoginPath);
                    dispatch(postLogout());
                    fetchAmzLogout();

                    return;
                }
                const result = await awsGetCurrentUser();
                const awsUserInfo = await awsFetchUserInfo();
                const signinRes = await amazeSignIn({ access_token: awsAccessToken }).unwrap();

                if (signinRes && awsUserInfo && result) {
                    const userInfo: ICurrentUser = {
                        accountId: signinRes.account_id,
                        isFirstTimeLogin: signinRes.is_first_time_login,
                        bindLine: signinRes.bind_line,
                        email: awsUserInfo?.email || "",
                        username: result.username,
                        enterpriseAccountId: null,
                        role: getUserRole(awsUserInfo?.email, usersRole),
                        enterpriseRoles: null,
                        enterpriseUserId: null,
                    };
                    dispatch(postLogin());
                    dispatch(setCurrentUser(userInfo));
                }
            } catch (error) {
                navigate(backLoginPath);
                dispatch(postLogout());
                fetchAmzLogout();
            }
        };

        if (!isEnterpriseLogin && !isLogin && !currentUser && (usersRole || userPermissionError)) {
            getAwsSession();
        }
    }, [
        amazeSignIn,
        backLoginPath,
        currentUser,
        dispatch,
        fetchAmzLogout,
        isEnterpriseLogin,
        isLogin,
        navigate,
        usersRole,
        userPermissionError,
    ]);

    // 權限清單（Worker）載入失敗時提醒使用者：角色／專案額度會暫時降為基本權限。
    useEffect(() => {
        if (userPermissionError && !permissionErrorNotifiedRef.current) {
            permissionErrorNotifiedRef.current = true;
            customToast.warn(
                t("COMMON.Permission_Load_Failed") ||
                    "權限資料暫時載入失敗，將以基本權限運作，部分功能與專案額度可能受限。"
            );
        }
    }, [userPermissionError, t]);

    if (isLoading || checkUserStatusLoading) {
        return <PageLoader loading={true} />;
    }
    return (
        <>
            <PopupProvider>
                <Outlet />
            </PopupProvider>
        </>
    );
};
export default VerifyLogin;
